import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PAPER_BASE } from './config';
import PaperSEO from '../../components/paper/PaperSEO';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const ITEMS_PER_PAGE = 12;

const formatDateRange = (start, end) => {
  if (!start) return '';
  if (!end || end === start) return start;
  return `${start} ~ ${end}`;
};

const CATEGORY_META = {
  FESTIVAL: { title: '축제', subtitle: '핑크페이퍼가 진행한 축제 프로젝트' },
  EVENT: { title: '공연', subtitle: '핑크페이퍼가 진행한 공연 프로젝트' },
};

export default function PaperWorkPage({ category }) {
  const [works, setWorks] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const meta = CATEGORY_META[category] || { title: '작품', subtitle: '' };

  useEffect(() => {
    setLoading(true);
    const params = { page: currentPage, limit: ITEMS_PER_PAGE };
    if (category) params.category = category;

    axios.get(`${API}/api/paperWork`, { params })
      .then(res => {
        setWorks(res.data.works || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalCount(res.data.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentPage, category]);

  useEffect(() => {
    setCurrentPage(1);
  }, [category]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="bg-[#0d0b14] min-h-screen">
      <PaperSEO
        title={`${meta.title} | 핑크페이퍼`}
        description={`${meta.subtitle}를 소개합니다`}
        path={category === 'FESTIVAL' ? '/festival' : '/event'}
      />
      {/* Title Section */}
      <section className="pt-10 pb-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-white text-2xl font-bold">{meta.title}</h1>
            {totalCount > 0 && (
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-400">
                전체 {totalCount}건
              </span>
            )}
          </div>
          {meta.subtitle && (
            <p className="text-slate-500 text-sm">{meta.subtitle}</p>
          )}
        </div>
      </section>

      {/* Card Grid */}
      <section className="pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-violet-400/30 border-t-violet-400 rounded-full animate-spin" />
            </div>
          ) : works.length === 0 ? (
            <div className="text-center py-20 text-white/30">등록된 작품이 없습니다.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {works.map((work) => (
                <Link
                  key={work.id}
                  to={`${PAPER_BASE}/work/${work.id}`}
                  className="group bg-[#1a1428] rounded-2xl border border-white/5 hover:border-white/15 hover:shadow-xl overflow-hidden transition-all duration-300">
                  {/* Image */}
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#221a38]">
                    <img
                      src={work.thumbnailUrl}
                      alt={work.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                      loading="lazy"
                    />
                    {work.tagline && (
                      <span className="absolute top-3 left-3 bg-gradient-to-r from-violet-600/90 to-fuchsia-500/90 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full backdrop-blur-sm shadow-lg line-clamp-1 max-w-[calc(100%-24px)]">
                        {work.tagline}
                      </span>
                    )}
                  </div>
                  {/* Content */}
                  <div className="p-5">
                    <span className="inline-block text-[10px] tracking-[0.15em] text-violet-400 uppercase font-medium mb-2">
                      {work.category === 'FESTIVAL' ? '축제' : work.category === 'EVENT' ? '공연' : work.category}
                    </span>
                    <h3 className="text-white text-base font-semibold mb-2 line-clamp-1 group-hover:text-violet-300 transition-colors">
                      {work.title}
                    </h3>
                    <div className="flex items-center gap-3 text-white/30 text-xs">
                      {work.workDate && <span>{formatDateRange(work.workDate, work.workDateEnd)}</span>}
                      {work.location && <span>{work.location}</span>}
                    </div>
                    {work.content && (
                      <p className="text-white/25 text-xs leading-relaxed mt-2 line-clamp-2 whitespace-pre-line">
                        {work.content.replace(/<[^>]*>/g, '')}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Pagination */}
      {totalPages > 1 && (
        <section className="pb-32">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-center gap-2">
            <button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-white/10 text-white/40 hover:border-violet-400/30 hover:text-violet-400 disabled:opacity-20 disabled:cursor-not-allowed transition-all">
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`w-10 h-10 flex items-center justify-center rounded-full text-sm transition-all ${
                  page === currentPage
                    ? 'bg-violet-400/20 text-violet-400 border border-violet-400/30'
                    : 'text-white/40 hover:text-white/60'
                }`}>
                {page}
              </button>
            ))}
            <button
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-white/10 text-white/40 hover:border-violet-400/30 hover:text-violet-400 disabled:opacity-20 disabled:cursor-not-allowed transition-all">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
